import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';
import { ServiceApplication, ServiceApplicationItem } from '../types/models';

export interface ServiceApplicationFilters {
  search?: string;
  status?: string;
  service_id?: number;
  customer_id?: number;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}

export interface CreateServiceApplicationData {
  customer_id: number;
  service_id: number;
  application_date?: string;
  reference_number?: string;
  notes?: string;
  items?: CreateApplicationItemData[];
}

export interface CreateApplicationItemData {
  billable_item_id: number;
  quantity: number;
  unit_price: number;
  total_amount: number;
  notes?: string;
}

export interface UpdateServiceApplicationData {
  application_date?: string;
  notes?: string;
  status?: 'pending' | 'approved' | 'rejected' | 'processing';
}

export interface ApproveApplicationData {
  approval_notes?: string;
  approved_amount?: number;
}

class ServiceApplicationService {
  private baseUrl = '/admin/county/service-applications';

  async getApplications(filters?: ServiceApplicationFilters): Promise<ApiResponse<PaginatedResponse<ServiceApplication>>> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.service_id) params.append('service_id', filters.service_id.toString());
    if (filters?.customer_id) params.append('customer_id', filters.customer_id.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());

    return apiClient.get<PaginatedResponse<ServiceApplication>>(`${this.baseUrl}?${params.toString()}`);
  }

  async getApplication(id: number): Promise<ApiResponse<ServiceApplication>> {
    return apiClient.get<ServiceApplication>(`${this.baseUrl}/${id}`);
  }

  async createApplication(data: CreateServiceApplicationData): Promise<ApiResponse<ServiceApplication>> {
    return apiClient.post<ServiceApplication>(this.baseUrl, data);
  }

  async updateApplication(id: number, data: UpdateServiceApplicationData): Promise<ApiResponse<ServiceApplication>> {
    return apiClient.put<ServiceApplication>(`${this.baseUrl}/${id}`, data);
  }

  async deleteApplication(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async approveApplication(id: number, data?: ApproveApplicationData): Promise<ApiResponse<ServiceApplication>> {
    return apiClient.post<ServiceApplication>(`${this.baseUrl}/${id}/approve`, data);
  }

  async rejectApplication(id: number, reason: string): Promise<ApiResponse<ServiceApplication>> {
    return apiClient.post<ServiceApplication>(`${this.baseUrl}/${id}/reject`, { reason });
  }

  async processApplication(id: number): Promise<ApiResponse<ServiceApplication>> {
    return apiClient.post<ServiceApplication>(`${this.baseUrl}/${id}/process`);
  }

  async getApplicationItems(id: number): Promise<ApiResponse<ServiceApplicationItem[]>> {
    return apiClient.get<ServiceApplicationItem[]>(`${this.baseUrl}/${id}/items`);
  }

  async addApplicationItem(id: number, data: CreateApplicationItemData): Promise<ApiResponse<ServiceApplicationItem>> {
    return apiClient.post<ServiceApplicationItem>(`${this.baseUrl}/${id}/items`, data);
  }

  async updateApplicationItem(applicationId: number, itemId: number, data: Partial<CreateApplicationItemData>): Promise<ApiResponse<ServiceApplicationItem>> {
    return apiClient.put<ServiceApplicationItem>(`${this.baseUrl}/${applicationId}/items/${itemId}`, data);
  }

  async deleteApplicationItem(applicationId: number, itemId: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/${applicationId}/items/${itemId}`);
  }

  async getApplicationsByCustomer(customerId: number, page?: number): Promise<ApiResponse<PaginatedResponse<ServiceApplication>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<ServiceApplication>>(`/county/customers/${customerId}/applications${params}`);
  }

  async getApplicationsByService(serviceId: number, page?: number): Promise<ApiResponse<PaginatedResponse<ServiceApplication>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<ServiceApplication>>(`/county/services/${serviceId}/applications${params}`);
  }
}

export const serviceApplicationService = new ServiceApplicationService();
export default serviceApplicationService;
